import { RANGE_LIST } from '@doda/common/src/constants/searchConditionModal';
import {
  HA_MASTER_ITEMS_LOWER,
  HA_MASTER_ITEMS_UPPER,
  NE_SEARCH_ITEMS,
  ES_SEARCH_ITEMS,
  HA_SEARCH_ITEMS,
} from '@doda/common/src/constants/searchItems';
import {
  appQueryParamsSelector,
  pageQuerySelector,
} from '@doda/common/src/redux/features/queryParams';
import { cassifyListAsLMS } from '@doda/common/src/utils/convArrayUtil';
import { escapeHtml } from '@doda/common/src/utils/escapeHtmlUtil';
import { getCommaSeparatedJobIds } from '@doda/common/src/logics/jobSearchList/getCommaSeparatedJobIds';
import Script from 'next/script';
import { FC } from 'react';
import { useAppSelector } from '@doda/pc/src/redux/hooks';
import {
  jobListSelector,
  numOfVideoSelector,
  resultCountSelector,
  totalJobCountSelector,
} from '@doda/pc/src/redux/features/jobList';

const PAGE_NAME_AND_CHANEL = '求人一覧（通常）';
const JOB_OFFER_KIND = { '0': '転職支援', '1': '全求人', '2': '直接応募' };
const INCLUDED_KEYWORD_CATEGORY = {
  '1': 'すべて',
  '2': '社名',
  '3': '職種名',
  '4': '勤務地',
  '9': 'スキル',
};
const MAX_MEASURE_JOB_COUNT = 20;

export const PageViewAnalytics: FC = () => {
  const { oc, ind, ar, pr, ha, op, k, kwc, tp, ci, wa, st, rt, ne, es, ds } =
    useAppSelector(appQueryParamsSelector);
  const resultCount = useAppSelector(resultCountSelector);
  const totalCount = useAppSelector(totalJobCountSelector);
  const primeCount = useAppSelector(numOfVideoSelector);
  const jobList = useAppSelector(jobListSelector);
  const pageNumber = useAppSelector(pageQuerySelector);

  // 求人が1件以上ある、かつ、1ページ目である、かつ、並び替え順がデフォルトである
  const includeEvar91 =
    totalCount >= 1 && pageNumber === 1 && (!ds || ds === '0');

  const { lList: ocL, mList: ocM, sList: ocS } = cassifyListAsLMS(oc ?? []);
  const { lList: indL, sList: indS } = cassifyListAsLMS(ind ?? []);

  const salaryLowerId = ha
    ? HA_MASTER_ITEMS_LOWER.find(
        (item) => item.id === (ha !== undefined ? ha[0] : '0')
      )?.masterId
    : '';
  const salaryUpperId = ha
    ? HA_MASTER_ITEMS_UPPER.find(
        (item) => item.id === (ha !== undefined ? ha[1] : '0')
      )?.masterId
    : '';
  const optionsIdList: string[] = [];
  if (op) {
    op.forEach((item) => {
      optionsIdList.push(item);
    });
  }
  if (typeof salaryLowerId === 'string' && salaryLowerId !== '') {
    optionsIdList.push(salaryLowerId);
  }
  if (typeof salaryUpperId === 'string' && salaryUpperId !== '') {
    optionsIdList.push(salaryUpperId);
  }
  if (ne?.length) {
    ne.forEach((neSimple) => {
      const nePush = NE_SEARCH_ITEMS.find(
        (item) => item.id === neSimple
      )?.masterId;
      if (nePush) optionsIdList.push(nePush);
    });
  }
  if (es?.length) {
    es.forEach((esSimple) => {
      const esPush = ES_SEARCH_ITEMS.find(
        (item) => item.id === esSimple
      )?.masterId;
      if (esPush) optionsIdList.push(esPush);
    });
  }
  // 閲覧数取得用イベント
  const events = ['event195'];
  if (primeCount) {
    // 動画求人有イベント
    events.push('event97');
    // 表示されている求人がすべて動画だった場合のイベント
    if (primeCount === resultCount) {
      events.push('event231');
    }
  }

  return (
    <Script strategy="beforeInteractive">
      {escapeHtml(`
        // AA
        if(!window.sc) {
          window.sc = {};
        }
        sc.pageName="${PAGE_NAME_AND_CHANEL}"
        sc.channel="${PAGE_NAME_AND_CHANEL}"
        sc.prop6="${ci || wa ? [...(ci || []), ...(wa || [])].join(',') : ''}"
        sc.prop7="${st ? st.join(',') : ''}"
        sc.prop9="${ocL ? ocL.join(',') : ''}"
        sc.prop10="${ocM ? ocM.join(',') : ''}"
        sc.prop11="${ocS ? ocS.join(',') : ''}"
        sc.prop12="${ar ? ar.join(',') : ''}"
        sc.prop13="${pr ? pr.join(',') : ''}"
        sc.prop14="${k ? `検索:${k}` : ''}"
        sc.prop15="${optionsIdList ? optionsIdList.join(',') : ''}"
        sc.prop16="${indL ? indL.join(',') : ''}"
        sc.prop17="${indS ? indS.join(',') : ''}"
        sc.prop20="${
          op
            ? op
                .filter((param) =>
                  ['17', '18', '19'].some((el) => el === param)
                )
                .join(',')
            : ''
        }",
        sc.prop48="${rt ? rt.join(',') : ''}"
        sc.prop50="${
          ha
            ? HA_SEARCH_ITEMS.find(
                (item) => item.id === (ha !== undefined ? ha[0] : '0')
              )?.name
            : ''
        }"
        sc.prop51="${
          ha
            ? HA_SEARCH_ITEMS.find(
                (item) => item.id === (ha !== undefined ? ha[1] : '0')
              )?.name
            : ''
        }"
        sc.prop52="${
          kwc ? RANGE_LIST.find((range) => kwc === range.value)?.label : ''
        }"
        sc.prop53="${tp ? JOB_OFFER_KIND[tp[0] as keyof typeof JOB_OFFER_KIND] : JOB_OFFER_KIND['1']}"
        sc.prop55="ページ内表示件数：${resultCount}"
        sc.eVar14="${k ? `検索:${k}` : ''}"
        sc.eVar56="${primeCount ? String(primeCount) : undefined}"
        sc.eVar84="${totalCount}"
        sc.eVar91="${includeEvar91 ? getCommaSeparatedJobIds(jobList, MAX_MEASURE_JOB_COUNT) : ''}"
        sc.eVar119="${primeCount ? 'prime' : undefined}"
        sc.linkTrackEvents="${events.join(',')}"
        sc.events="${events.join(',')}"

        // GA
        window.dataLayer = window.dataLayer || [];
        dataLayer.push({
          PageName: '${PAGE_NAME_AND_CHANEL}',
          Channel: '${PAGE_NAME_AND_CHANEL}',
          BizLargeClass: "${ocL ? ocL.join(',') : ''}",
          BizMiddleClass: "${ocM ? ocM.join(',') : ''}",
          BizSmallClass: "${ocS ? ocS.join(',') : ''}",
          WorkArea: "${ar ? ar.join(',') : ''}",
          WorkPrefecture: "${pr ? pr.join(',') : ''}",
          FreeWordSearch: "${k ?? ''}",
          SearchOption: "${optionsIdList ? optionsIdList.join(',') : ''}",
          IndLargeClass: "${indL ? indL.join(',') : ''}",
          IndSmallClass: "${indS ? indS.join(',') : ''}",
          EmpPattern: "${
            op
              ? op
                  .filter((param) =>
                    ['17', '18', '19'].some((el) => el === param)
                  )
                  .join(',')
              : ''
          }",
          WishLowSalary: "${salaryLowerId}",
          WishUppSalary: "${salaryUpperId}",
          KeywordSearchSeg: "${kwc ? INCLUDED_KEYWORD_CATEGORY[kwc[0] as keyof typeof INCLUDED_KEYWORD_CATEGORY] : ''}",
          JobListTab: "${tp ? JOB_OFFER_KIND[tp[0] as keyof typeof JOB_OFFER_KIND] : JOB_OFFER_KIND['1']}"
        });
        `)}
    </Script>
  );
};
